import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import API from '../config/api';
import QuratorLayout from '../components/qurator/QuratorLayout';
import Swal from 'sweetalert2';
import './Calendar.css';

/* ─── Portal Tooltip ─── */
const Tooltip = ({ text, children, position = 'top' }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, pos: 'top' });

  const show = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const left = rect.left + rect.width / 2;
    const top  = position === 'bottom' ? rect.bottom + 10 : rect.top - 10;
    setCoords({ top, left, pos: position === 'bottom' ? 'bottom' : 'top' });
    setVisible(true);
  }, [position]);

  const hide = useCallback(() => setVisible(false), []);
  const child = React.Children.only(children);
  const enhanced = React.cloneElement(child, {
    onMouseEnter: (e) => { show(e); child.props.onMouseEnter?.(e); },
    onMouseLeave: (e) => { hide();  child.props.onMouseLeave?.(e); },
    onFocus:      (e) => { show(e); child.props.onFocus?.(e); },
    onBlur:       (e) => { hide();  child.props.onBlur?.(e); },
  });
  return (
    <>
      {enhanced}
      {visible && ReactDOM.createPortal(
        <div className={`cal-tooltip cal-tooltip--${coords.pos}`}
          style={{ top: coords.top, left: coords.left }}>{text}</div>,
        document.body
      )}
    </>
  );
};

/* ─── Modal Overlay — defined OUTSIDE component so React never treats it as
       a "new" component type on re-render (prevents unmount/remount flicker) ─── */
const ModalOverlay = ({ onClose, children }) => (
  <div className="cal-modal-overlay" onClick={onClose}>
    <div className="cal-modal" onClick={e => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

/* ─── Status config (lemon yellow for today) ─── */
const STATUS_CFG = {
  completed: { label: 'Completed', color: '#16a34a', bg: 'rgba(22,163,74,0.11)',   border: 'rgba(22,163,74,0.55)',   icon: 'check-circle-fill' },
  missed:    { label: 'Missed',    color: '#dc2626', bg: 'rgba(220,38,38,0.10)',   border: 'rgba(220,38,38,0.55)',   icon: 'x-circle-fill' },
  upcoming:  { label: 'Upcoming',  color: '#2563eb', bg: 'rgba(37,99,235,0.10)',   border: 'rgba(37,99,235,0.55)',   icon: 'calendar-check-fill' },
  today:     { label: 'Today',     color: '#9a7d00', bg: 'rgba(255,236,0,0.15)',   border: 'rgba(220,196,0,0.65)',   icon: 'calendar-event-fill' },
};
const ALL_STATUSES = Object.keys(STATUS_CFG);

const Calendar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const calendarInstanceRef = useRef(null);
  // Refs track values without re-creating loadCalendarEvents
  const filterValueRef    = useRef('organization');
  const statusFiltersRef  = useRef(ALL_STATUSES); // all selected by default
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [organizationName, setOrganizationName] = useState('Organization');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [filterValue, setFilterValue] = useState('organization');
  const [calendarView, setCalendarView] = useState('dayGridMonth');

  /* Modal states */
  const [showScheduleModal, setShowScheduleModal]     = useState(false);
  const [showDateClickModal, setShowDateClickModal]   = useState(false);
  const [showDeleteModal, setShowDeleteModal]         = useState(false);
  const [showDetailModal, setShowDetailModal]         = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);

  /* Form states */
  const [assignmentType, setAssignmentType]   = useState('individual');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedTeam, setSelectedTeam]       = useState('');
  const [selectedExperiment, setSelectedExperiment] = useState('');
  const [selectedUser, setSelectedUser]       = useState('');
  const [selectedDate, setSelectedDate]       = useState('');
  const [clickedDate, setClickedDate]         = useState('');
  const [experiments, setExperiments]         = useState([]);
  const [teamUsers, setTeamUsers]             = useState([]);
  const [userExperiments, setUserExperiments] = useState({});
  const [dateClickUserExperiments, setDateClickUserExperiments] = useState({});

  /* Status filter chips — default: all selected */
  const [statusFilters, setStatusFilters] = useState(ALL_STATUSES);

  /* Other */
  const [scheduleToDelete, setScheduleToDelete]       = useState(null);
  const [scheduleToDeleteInfo, setScheduleToDeleteInfo] = useState(null); // {name, user, date}
  const [selectedEvent, setSelectedEvent]             = useState(null);
  const [rescheduleScheduleId, setRescheduleScheduleId] = useState(null);
  const [currentScheduleDate, setCurrentScheduleDate] = useState('');
  const [newScheduleDate, setNewScheduleDate]         = useState('');

  /* ─── Helpers ─── */
  const showAlert = useCallback((type, message) => {
    if (type === 'success') {
      Swal.fire({ icon: 'success', title: 'Success', text: message,
        timer: 2500, showConfirmButton: false, toast: true, position: 'top-end',
        customClass: { popup: 'cal-swal-toast' } });
    } else {
      Swal.fire({ icon: 'error', title: 'Oops!', text: message,
        confirmButtonColor: '#FF5722', confirmButtonText: 'Got it' });
    }
  }, []);

  const escapeHtml = useCallback((text) => {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }, []);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' });
  }, []);

  /* ─── Load events (STABLE — reads filterValue from ref, never recreated) ─── */
  const loadCalendarEvents = useCallback(async (info, successCallback, failureCallback) => {
    try {
      const fv = filterValueRef.current;
      let filterType = 'organization', filterVal = '';
      if (fv === 'my')                       { filterType = 'my'; }
      else if (fv.startsWith('project-'))    { filterType = 'project'; filterVal = fv.split('-')[1]; }
      else if (fv.startsWith('user-'))       { filterType = 'user';    filterVal = fv.split('-')[1]; }
      const response = await API.get('/qurator/calendar/events', {
        params: { filter_type: filterType, filter_value: filterVal }
      });
      // Filter by selected statuses
      const active = statusFiltersRef.current;
      const filtered = response.data.filter(ev => {
        const st = ev.extendedProps?.status || 'upcoming';
        return active.includes(st);
      });
      successCallback(filtered);
    } catch (error) {
      console.error('Error loading calendar events:', error);
      failureCallback(error);
    }
  }, []); // No deps — stable reference, reads from refs

  /* ─── Initial data fetch ─── */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const userRes = await API.get('/auth/verify');
        setCurrentUserId(userRes.data.user.user_id);
        setOrganizationName('Organization');
        const [projectsRes, usersRes, teamsRes] = await Promise.all([
          API.get('/qurator/calendar/projects'),
          API.get('/qurator/calendar/users'),
          API.get('/qurator/calendar/teams'),
        ]);
        setProjects(projectsRes.data);
        setUsers(usersRes.data);
        setTeams(teamsRes.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        showAlert('danger', 'Failed to load calendar data');
        setLoading(false);
      }
    };
    fetchData();
  }, [showAlert]);

  /* ─── URL params ─── */
  useEffect(() => {
    const projectId = searchParams.get('project_id');
    const teamId    = searchParams.get('team_id');
    const userId    = searchParams.get('user_id');
    const openModal = searchParams.get('open_modal') === 'true';
    if (projectId || teamId || userId) {
      const loadData = async () => {
        if (projectId) {
          setSelectedProject(projectId);
          try { const r = await API.get(`/qurator/calendar/projects/${projectId}/experiments`); setExperiments(r.data); } catch {}
        }
        if (teamId) {
          setSelectedTeam(teamId); setAssignmentType('team');
          try {
            const r = await API.get(`/qurator/calendar/teams/${teamId}/users`);
            setTeamUsers(r.data);
            const init = {}; r.data.forEach(u => { init[u.user_id] = ''; });
            setUserExperiments(init); setDateClickUserExperiments(init);
          } catch {}
        }
        if (userId) { setSelectedUser(userId); setAssignmentType('individual'); }
        if (openModal) setTimeout(() => setShowScheduleModal(true), 500);
        const np = new URLSearchParams(searchParams);
        ['project_id','team_id','user_id','open_modal'].forEach(k => np.delete(k));
        setSearchParams(np, { replace: true });
      };
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ─── Init FullCalendar ─── */
  useEffect(() => {
    if (!loading && calendarRef.current && !calendarInstanceRef.current) {
      if (!window.FullCalendar) { showAlert('danger', 'Calendar library not loaded. Please refresh.'); return; }
      if (calendarInstanceRef.current) { calendarInstanceRef.current.destroy(); calendarInstanceRef.current = null; }
      try {
        // On narrow screens, list view is far more usable than a grid
        const isMobile = window.innerWidth <= 640;
        const startView = isMobile ? 'listWeek' : calendarView;
        if (isMobile) setCalendarView('listWeek');

        const calendar = new window.FullCalendar.Calendar(calendarRef.current, {
          initialView: startView,
          headerToolbar: { left: 'prev,next today', center: 'title', right: '' },
          height: 'auto', contentHeight: 'auto', aspectRatio: 1.8,
          // Responsive: on resize across breakpoint, switch view
          windowResize: () => {
            if (!calendarInstanceRef.current) return;
            const mobile = window.innerWidth <= 640;
            const cur = calendarInstanceRef.current.view.type;
            if (mobile && (cur === 'dayGridMonth' || cur === 'dayGridWeek')) {
              calendarInstanceRef.current.changeView('listWeek');
              setCalendarView('listWeek');
            } else if (!mobile && cur === 'listWeek') {
              calendarInstanceRef.current.changeView('dayGridMonth');
              setCalendarView('dayGridMonth');
            }
          },
          events: loadCalendarEvents,
          dateClick: (info) => {
            setClickedDate(info.dateStr); setSelectedDate(info.dateStr);
            setShowDateClickModal(true);
          },
          eventClick: async (info) => {
            if (info.jsEvent?.target.closest('.cal-pill-del')) return;
            const event = info.event;
            const base = { id: event.id, title: event.title, start: event.start,
              startStr: event.startStr, extendedProps: { ...event.extendedProps } };
            setSelectedEvent(base); setShowDetailModal(true);
            try {
              const r = await API.get(`/qurator/calendar/schedule-details/${event.id}`);
              if (r.data.success) {
                setSelectedEvent({ ...base, schedule: r.data.schedule,
                  extendedProps: { ...base.extendedProps, status: base.extendedProps?.status || 'upcoming' } });
              }
            } catch {}
          },
          eventContent: (arg) => {
            const event  = arg.event;
            const status = event.extendedProps.status || 'upcoming';
            const canDel = event.extendedProps.created_by_id === currentUserId;
            const user   = event.extendedProps.user || '';
            const dateStr = event.startStr ? event.startStr.split('T')[0] : '';
            return {
              html: `<div class="cal-pill cal-pill--${status}">
                <span class="cal-pill-dot"></span>
                <div class="cal-pill-info">
                  <span class="cal-pill-title">${escapeHtml(event.title)}</span>
                  ${user ? `<span class="cal-pill-user"><i class="bi bi-person-fill"></i> ${escapeHtml(user)}</span>` : ''}
                </div>
                ${canDel ? `<button class="cal-pill-del" 
                  data-schedule-id="${event.id}"
                  data-exp-name="${escapeHtml(event.title)}"
                  data-exp-user="${escapeHtml(user)}"
                  data-exp-date="${escapeHtml(dateStr)}"
                  title="Delete this schedule">
                  <i class="bi bi-trash-fill"></i>
                </button>` : ''}
              </div>`
            };
          },
          eventDidMount: (arg) => {
            arg.el.style.cursor = 'pointer';
            const btn = arg.el.querySelector('.cal-pill-del');
            if (btn) {
              btn.addEventListener('click', (e) => {
                e.stopPropagation(); e.preventDefault();
                const sid  = btn.getAttribute('data-schedule-id');
                const name = btn.getAttribute('data-exp-name');
                const user = btn.getAttribute('data-exp-user');
                const date = btn.getAttribute('data-exp-date');
                if (sid) window.dispatchEvent(new CustomEvent('deleteSchedule', {
                  detail: { scheduleId: parseInt(sid), name, user, date }
                }));
              });
            }
          }
        });
        calendar.render();
        calendarInstanceRef.current = calendar;
      } catch (error) {
        console.error('Error initializing calendar:', error);
        showAlert('danger', 'Failed to initialize calendar. Please refresh.');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]); // Only depends on loading — calendar initialized once after data loads

  /* ─── Delete event listener ─── */
  useEffect(() => {
    const handler = (e) => {
      const { scheduleId, name, user, date } = e.detail;
      setScheduleToDelete(scheduleId);
      setScheduleToDeleteInfo({ name: name || 'Experiment', user: user || 'N/A', date: date || '' });
      setShowDeleteModal(true);
    };
    window.addEventListener('deleteSchedule', handler);
    return () => window.removeEventListener('deleteSchedule', handler);
  }, []);

  /* ─── Status filter toggle ─── */
  const toggleStatusFilter = (status) => {
    setStatusFilters(prev => {
      let next;
      if (prev.includes(status)) {
        next = prev.length === 1 ? ALL_STATUSES : prev.filter(s => s !== status);
      } else {
        next = [...prev, status];
      }
      statusFiltersRef.current = next;
      calendarInstanceRef.current?.refetchEvents();
      return next;
    });
  };

  /* ─── Actions ─── */
  const handleFilterChange = (e) => {
    const newFilter = e.target.value;
    filterValueRef.current = newFilter; // update ref FIRST so refetchEvents reads latest
    setFilterValue(newFilter);          // update state for UI re-render
    // Refetch directly — no separate useEffect needed, no flicker
    if (calendarInstanceRef.current) {
      calendarInstanceRef.current.refetchEvents();
    }
  };
  const changeCalendarView = (view) => {
    setCalendarView(view);
    calendarInstanceRef.current?.changeView(view);
  };

  const loadExperiments = async (projectId) => {
    if (!projectId) { setExperiments([]); return; }
    try { const r = await API.get(`/qurator/calendar/projects/${projectId}/experiments`); setExperiments(r.data); }
    catch { setExperiments([]); }
  };

  const loadTeamUsers = async (teamId) => {
    if (!teamId) { setTeamUsers([]); setUserExperiments({}); setDateClickUserExperiments({}); return; }
    try {
      const r = await API.get(`/qurator/calendar/teams/${teamId}/users`);
      setTeamUsers(r.data);
      const init = {}; r.data.forEach(u => { init[u.user_id] = ''; });
      setUserExperiments(init); setDateClickUserExperiments(init);
    } catch { setTeamUsers([]); }
  };

  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      let payload = { scheduled_date: selectedDate };
      if (assignmentType === 'individual') {
        if (!selectedProject || !selectedExperiment || !selectedUser || !selectedDate) { showAlert('danger', 'Please fill in all required fields'); return; }
        payload = { assignment_type: 'individual', project_id: selectedProject, experiment_id: selectedExperiment, user_id: selectedUser, scheduled_date: selectedDate };
      } else {
        if (!selectedProject || !selectedTeam || !selectedDate) { showAlert('danger', 'Please fill in all required fields'); return; }
        if (!Object.values(userExperiments).some(v => v)) { showAlert('danger', 'Please assign at least one experiment to a team member'); return; }
        payload = { assignment_type: 'team', project_id: selectedProject, team_id: selectedTeam, scheduled_date: selectedDate, user_experiments: userExperiments };
      }
      const r = await API.post('/qurator/calendar/schedule', payload);
      if (r.data.success) {
        showAlert('success', r.data.message); setShowScheduleModal(false); resetScheduleForm();
        calendarInstanceRef.current?.refetchEvents();
      }
    } catch (error) { showAlert('danger', error.response?.data?.message || 'Failed to schedule experiment'); }
  };

  const handleDateClickSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProject || !selectedTeam || !clickedDate) { showAlert('danger', 'Please fill in all required fields'); return; }
    if (!Object.values(dateClickUserExperiments).some(v => v)) { showAlert('danger', 'Please assign at least one experiment to a team member'); return; }
    try {
      const r = await API.post('/qurator/calendar/schedule', {
        assignment_type: 'team', project_id: selectedProject, team_id: selectedTeam,
        scheduled_date: clickedDate, user_experiments: dateClickUserExperiments
      });
      if (r.data.success) {
        showAlert('success', r.data.message); setShowDateClickModal(false); resetDateClickForm();
        calendarInstanceRef.current?.refetchEvents();
      }
    } catch (error) { showAlert('danger', error.response?.data?.message || 'Failed to schedule experiment'); }
  };

  const handleDelete = async () => {
    if (!scheduleToDelete) return;
    try {
      const r = await API.delete(`/qurator/calendar/schedule/${scheduleToDelete}`);
      if (r.data.success) {
        showAlert('success', r.data.message); setShowDeleteModal(false); setScheduleToDelete(null);
        calendarInstanceRef.current?.refetchEvents();
      }
    } catch (error) { showAlert('danger', error.response?.data?.message || 'Failed to delete schedule'); }
  };

  const openRescheduleModal = (scheduleId, currentDate) => {
    setRescheduleScheduleId(scheduleId); setCurrentScheduleDate(currentDate);
    setNewScheduleDate(''); setShowRescheduleModal(true); setShowDetailModal(false);
  };

  const handleReschedule = async () => {
    if (!rescheduleScheduleId || !newScheduleDate) { showAlert('danger', 'Please select a new date'); return; }
    try {
      const r = await API.put(`/qurator/calendar/reschedule/${rescheduleScheduleId}`, { new_date: newScheduleDate });
      if (r.data.success) {
        showAlert('success', r.data.message); setShowRescheduleModal(false);
        setRescheduleScheduleId(null); setNewScheduleDate('');
        calendarInstanceRef.current?.refetchEvents();
      }
    } catch (error) { showAlert('danger', error.response?.data?.message || 'Failed to reschedule experiment'); }
  };

  const viewAnalytics = (scheduleId) => navigate(`/analytics?schedule_id=${scheduleId}`);

  const resetScheduleForm = () => {
    setAssignmentType('individual'); setSelectedProject(''); setSelectedTeam('');
    setSelectedExperiment(''); setSelectedUser(''); setSelectedDate('');
    setExperiments([]); setTeamUsers([]); setUserExperiments({});
  };
  const resetDateClickForm = () => {
    setSelectedProject(''); setSelectedTeam(''); setClickedDate('');
    setTeamUsers([]); setDateClickUserExperiments({});
  };

  /* ─── Derived detail values ─── */
  const derivedProjectName    = selectedEvent?.schedule?.project_name    || selectedEvent?.extendedProps?.project || 'N/A';
  const derivedExperimentName = selectedEvent?.schedule?.experiment_name || selectedEvent?.title || 'N/A';
  const derivedAssignedTo     = selectedEvent?.schedule?.user_name       || selectedEvent?.extendedProps?.user || 'N/A';
  const derivedTeam           = selectedEvent?.schedule?.team_name       || selectedEvent?.extendedProps?.team || '';
  const derivedDateRaw        = selectedEvent?.schedule?.scheduled_date  || selectedEvent?.startStr || '';
  const derivedDate           = derivedDateRaw ? formatDate(derivedDateRaw) : 'N/A';
  const derivedStatusRaw      = selectedEvent?.extendedProps?.status     || 'upcoming';
  const derivedStatus         = (derivedStatusRaw || 'upcoming').toLowerCase();
  const derivedFilename       = selectedEvent?.schedule?.filename;
  const sCfg = STATUS_CFG[derivedStatus] || STATUS_CFG.upcoming;


  if (loading) {
    return (
      <QuratorLayout>
        <div className="cal-page">
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', gap:'1rem', color:'#718096' }}>
            <div className="qrt-loading"></div>
            <p>Loading calendar...</p>
          </div>
        </div>
      </QuratorLayout>
    );
  }

  return (
    <QuratorLayout>
      <div className="cal-page">

        {/* ── Hero ── */}
        <div className="cal-hero">
          <div className="cal-hero-inner">
            <div>
              <h1 className="cal-hero-title"><i className="bi bi-calendar3-range-fill"></i> Experiment Calendar</h1>
              <p className="cal-hero-sub">Schedule and manage experiments across your organization</p>
            </div>
            <div className="cal-hero-actions">
              <Tooltip text="Schedule a new experiment">
                <button className="cal-hero-btn" onClick={() => setShowScheduleModal(true)}>
                  <i className="bi bi-plus-circle-fill"></i> Schedule
                </button>
              </Tooltip>
            </div>
            <i className="bi bi-calendar3 cal-hero-bg-icon d-none d-lg-block"></i>
          </div>
        </div>

        {/* ── Status Filter Chips + Filter + View ── */}
        <div className="cal-toolbar">
          {/* Status filter chips — click to toggle, all on by default */}
          <div className="cal-status-filters">
            {Object.entries(STATUS_CFG).map(([k, v]) => {
              const active = statusFilters.includes(k);
              return (
                <Tooltip key={k} text={active ? `Hide ${v.label}` : `Show ${v.label}`} position="top">
                  <button
                    className={`cal-status-chip ${active ? 'active' : 'inactive'}`}
                    style={{
                      '--chip-color':    v.color,
                      '--chip-border':   v.border,
                      '--chip-bg':       v.bg,
                    }}
                    onClick={() => toggleStatusFilter(k)}
                  >
                    <i className={`bi bi-${v.icon}`}></i>
                    {v.label}
                  </button>
                </Tooltip>
              );
            })}
          </div>

          <div className="cal-toolbar-right">
            {/* Filter */}
            <div className="cal-filter-wrap">
              <i className="bi bi-funnel cal-filter-icon"></i>
              <select className="cal-filter-select" value={filterValue} onChange={handleFilterChange}>
                <option value="organization">{organizationName}</option>
                <option value="my">My Schedules</option>
                <optgroup label="Projects">
                  {projects.map(p => <option key={p.project_id} value={`project-${p.project_id}`}>{p.project_name}</option>)}
                </optgroup>
                <optgroup label="Users">
                  {users.map(u => <option key={u.user_id} value={`user-${u.user_id}`}>{u.first_name} {u.last_name}</option>)}
                </optgroup>
              </select>
            </div>

            {/* View toggle */}
            <div className="cal-view-toggle">
              <Tooltip text="List view (best for mobile)">
                <button className={`cal-view-btn ${calendarView === 'listWeek' ? 'active' : ''}`} onClick={() => changeCalendarView('listWeek')}>
                  <i className="bi bi-list-ul"></i> <span className="cal-view-label">List</span>
                </button>
              </Tooltip>
              <Tooltip text="Week view">
                <button className={`cal-view-btn ${calendarView === 'dayGridWeek' ? 'active' : ''}`} onClick={() => changeCalendarView('dayGridWeek')}>
                  <i className="bi bi-calendar-week"></i> <span className="cal-view-label">Week</span>
                </button>
              </Tooltip>
              <Tooltip text="Month view">
                <button className={`cal-view-btn ${calendarView === 'dayGridMonth' ? 'active' : ''}`} onClick={() => changeCalendarView('dayGridMonth')}>
                  <i className="bi bi-calendar-month"></i> <span className="cal-view-label">Month</span>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* ── Calendar ── */}
        <div className="cal-grid-wrap">
          <div id="expCalendar" ref={calendarRef}></div>
        </div>

      </div>

      {/* ════════ SCHEDULE MODAL ════════ */}
      {showScheduleModal && (
        <ModalOverlay onClose={() => { setShowScheduleModal(false); resetScheduleForm(); }}>
          <div className="cal-modal-header">
            <div>
              <h3 className="cal-modal-title"><i className="bi bi-calendar-plus-fill"></i> Schedule Experiment</h3>
              <p className="cal-modal-sub">Assign an experiment to an individual or team</p>
            </div>
            <button className="cal-modal-close" onClick={() => { setShowScheduleModal(false); resetScheduleForm(); }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div className="cal-modal-body">
            <form onSubmit={handleScheduleSubmit}>
              <div className="cal-assign-types">
                {[{id:'individual',icon:'person-circle',label:'Individual',sub:'Assign to single user'},
                  {id:'team',icon:'people-fill',label:'Team',sub:'Assign to team members'}].map(t => (
                  <div key={t.id} className={`cal-assign-card ${assignmentType===t.id?'active':''}`} onClick={() => setAssignmentType(t.id)}>
                    <i className={`bi bi-${t.icon}`}></i>
                    <strong>{t.label}</strong>
                    <span>{t.sub}</span>
                  </div>
                ))}
              </div>

              <div className="cal-form-group">
                <label className="cal-form-label">Select Project *</label>
                <select className="cal-form-control" value={selectedProject} onChange={e => { setSelectedProject(e.target.value); loadExperiments(e.target.value); }} required>
                  <option value="">Choose a project...</option>
                  {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_name}</option>)}
                </select>
              </div>

              {assignmentType === 'team' && (
                <div className="cal-form-group">
                  <label className="cal-form-label">Select Team *</label>
                  <select className="cal-form-control" value={selectedTeam} onChange={e => { setSelectedTeam(e.target.value); loadTeamUsers(e.target.value); }} required>
                    <option value="">Choose a team...</option>
                    {teams.map(t => <option key={t.group_id} value={t.group_id}>{t.name}</option>)}
                  </select>
                </div>
              )}

              {assignmentType === 'individual' && (
                <>
                  <div className="cal-form-group">
                    <label className="cal-form-label">Select Experiment *</label>
                    <select className="cal-form-control" value={selectedExperiment} onChange={e => setSelectedExperiment(e.target.value)} required disabled={!selectedProject}>
                      <option value="">{selectedProject ? 'Choose an experiment...' : 'First select a project...'}</option>
                      {experiments.map(e => <option key={e.prt_id} value={e.prt_id}>{e.draft_title}</option>)}
                    </select>
                  </div>
                  <div className="cal-form-group">
                    <label className="cal-form-label">Assign to User *</label>
                    <select className="cal-form-control" value={selectedUser} onChange={e => setSelectedUser(e.target.value)} required>
                      <option value="">Choose a user...</option>
                      {users.map(u => <option key={u.user_id} value={u.user_id}>{u.first_name} {u.last_name}</option>)}
                    </select>
                  </div>
                </>
              )}

              {assignmentType === 'team' && selectedTeam && (
                <div className="cal-form-group">
                  <label className="cal-form-label">Assign Experiments to Team Members</label>
                  <div className="cal-team-users">
                    {teamUsers.length > 0 ? teamUsers.map(u => (
                      <div key={u.user_id} className="cal-team-user-row">
                        <div className="cal-user-avatar">{u.first_name[0]}{u.last_name[0]}</div>
                        <div className="cal-user-info">
                          <div className="cal-user-name">{u.first_name} {u.last_name}</div>
                          <div className="cal-user-email">{u.email}</div>
                        </div>
                        <select className="cal-form-control cal-user-exp-select" value={userExperiments[u.user_id]||''} onChange={e => setUserExperiments({...userExperiments,[u.user_id]:e.target.value})}>
                          <option value="">Select experiment...</option>
                          {experiments.map(ex => <option key={ex.prt_id} value={ex.prt_id}>{ex.draft_title}</option>)}
                        </select>
                      </div>
                    )) : <p className="cal-empty-msg">Select a team to see members</p>}
                  </div>
                </div>
              )}

              <div className="cal-form-group">
                <label className="cal-form-label">Schedule Date *</label>
                <input type="date" className="cal-form-control" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} min={new Date().toISOString().split('T')[0]} required />
              </div>

              <button type="submit" className="cal-btn-submit">
                <i className="bi bi-calendar-check-fill"></i> Schedule Experiment
              </button>
            </form>
          </div>
        </ModalOverlay>
      )}

      {/* ════════ DATE CLICK MODAL ════════ */}
      {showDateClickModal && (
        <ModalOverlay onClose={() => { setShowDateClickModal(false); resetDateClickForm(); }}>
          <div className="cal-modal-header">
            <div>
              <h3 className="cal-modal-title"><i className="bi bi-calendar-event-fill"></i> Schedule for {formatDate(clickedDate)}</h3>
              <p className="cal-modal-sub">Assign experiments to your team on this date</p>
            </div>
            <button className="cal-modal-close" onClick={() => { setShowDateClickModal(false); resetDateClickForm(); }}><i className="bi bi-x-lg"></i></button>
          </div>
          <div className="cal-modal-body">
            <form onSubmit={handleDateClickSubmit}>
              <div className="cal-form-group">
                <label className="cal-form-label">Select Project *</label>
                <select className="cal-form-control" value={selectedProject} onChange={e => { setSelectedProject(e.target.value); loadExperiments(e.target.value); }} required>
                  <option value="">Choose a project...</option>
                  {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_name}</option>)}
                </select>
              </div>
              <div className="cal-form-group">
                <label className="cal-form-label">Select Team *</label>
                <select className="cal-form-control" value={selectedTeam} onChange={e => { setSelectedTeam(e.target.value); loadTeamUsers(e.target.value); }} required>
                  <option value="">Choose a team...</option>
                  {teams.map(t => <option key={t.group_id} value={t.group_id}>{t.name}</option>)}
                </select>
              </div>
              {selectedTeam && (
                <div className="cal-form-group">
                  <label className="cal-form-label">Assign Experiments to Team Members</label>
                  <div className="cal-team-users">
                    {teamUsers.length > 0 ? teamUsers.map(u => (
                      <div key={u.user_id} className="cal-team-user-row">
                        <div className="cal-user-avatar">{u.first_name[0]}{u.last_name[0]}</div>
                        <div className="cal-user-info">
                          <div className="cal-user-name">{u.first_name} {u.last_name}</div>
                          <div className="cal-user-email">{u.email}</div>
                        </div>
                        <select className="cal-form-control cal-user-exp-select" value={dateClickUserExperiments[u.user_id]||''} onChange={e => setDateClickUserExperiments({...dateClickUserExperiments,[u.user_id]:e.target.value})}>
                          <option value="">Select experiment...</option>
                          {experiments.map(ex => <option key={ex.prt_id} value={ex.prt_id}>{ex.draft_title}</option>)}
                        </select>
                      </div>
                    )) : <p className="cal-empty-msg">Select a team to see members</p>}
                  </div>
                </div>
              )}
              <button type="submit" className="cal-btn-submit">
                <i className="bi bi-calendar-check-fill"></i> Schedule Experiment
              </button>
            </form>
          </div>
        </ModalOverlay>
      )}

      {/* ════════ DELETE MODAL ════════ */}
      {showDeleteModal && (
        <ModalOverlay onClose={() => { setShowDeleteModal(false); setScheduleToDelete(null); setScheduleToDeleteInfo(null); }}>
          <div className="cal-modal-header cal-modal-header--danger">
            <div>
              <h3 className="cal-modal-title"><i className="bi bi-trash-fill"></i> Delete Schedule</h3>
              <p className="cal-modal-sub">This action cannot be undone</p>
            </div>
            <button className="cal-modal-close" onClick={() => { setShowDeleteModal(false); setScheduleToDelete(null); setScheduleToDeleteInfo(null); }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div className="cal-modal-body" style={{ textAlign:'center', padding:'2rem 2rem 1.5rem' }}>
            <div className="cal-delete-icon"><i className="bi bi-calendar-x-fill"></i></div>
            <p style={{ color:'#4a5568', fontSize:'1rem', fontWeight:600, marginBottom:'1.25rem' }}>
              Are you sure you want to delete this scheduled experiment?
            </p>
            {scheduleToDeleteInfo && (
              <div className="cal-delete-info">
                <div className="cal-delete-info-row">
                  <i className="bi bi-cpu-fill"></i>
                  <div>
                    <div className="cal-delete-info-label">Experiment</div>
                    <div className="cal-delete-info-val">{scheduleToDeleteInfo.name}</div>
                  </div>
                </div>
                <div className="cal-delete-info-row">
                  <i className="bi bi-person-fill"></i>
                  <div>
                    <div className="cal-delete-info-label">Assigned To</div>
                    <div className="cal-delete-info-val">{scheduleToDeleteInfo.user}</div>
                  </div>
                </div>
                {scheduleToDeleteInfo.date && (
                  <div className="cal-delete-info-row">
                    <i className="bi bi-calendar-event-fill"></i>
                    <div>
                      <div className="cal-delete-info-label">Date</div>
                      <div className="cal-delete-info-val">{formatDate(scheduleToDeleteInfo.date)}</div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="cal-modal-footer">
            <button className="cal-btn-cancel" onClick={() => { setShowDeleteModal(false); setScheduleToDelete(null); setScheduleToDeleteInfo(null); }}>
              <i className="bi bi-x-circle"></i> Cancel
            </button>
            <button className="cal-btn-danger" onClick={handleDelete}>
              <i className="bi bi-trash-fill"></i> Delete Schedule
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* ════════ DETAIL MODAL ════════ */}
      {showDetailModal && selectedEvent && (
        <ModalOverlay onClose={() => { setShowDetailModal(false); setSelectedEvent(null); }}>
          <div className="cal-modal-header" style={{ background:`linear-gradient(135deg, ${sCfg.color}dd, ${sCfg.color}99)` }}>
            <div>
              <div className="cal-detail-status-badge">
                <i className={`bi bi-${sCfg.icon}`}></i> {sCfg.label}
              </div>
              <h3 className="cal-modal-title" style={{ marginTop:'0.5rem' }}>{derivedExperimentName}</h3>
              <p className="cal-modal-sub">{derivedDate}</p>
            </div>
            <button className="cal-modal-close" style={{ color:'white', background:'rgba(255,255,255,0.2)' }} onClick={() => { setShowDetailModal(false); setSelectedEvent(null); }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div className="cal-modal-body">
            <div className="cal-detail-grid">
              {[
                { icon:'folder-fill', label:'Project', value: derivedProjectName, color:'#2563eb', bg:'#dbeafe' },
                { icon:'cpu-fill', label:'Experiment', value: derivedExperimentName, color:'#d97706', bg:'#fef3c7' },
                { icon:'person-check-fill', label:'Assigned To', value: derivedAssignedTo, color:'#7c3aed', bg:'#ede9fe' },
                ...(derivedTeam ? [{ icon:'people-fill', label:'Team', value: derivedTeam, color:'#0891b2', bg:'#e0f2fe' }] : []),
                { icon:'calendar-event-fill', label:'Scheduled Date', value: derivedDate, color:'#4f46e5', bg:'#e0e7ff' },
              ].map((row, i) => (
                <div key={i} className="cal-detail-row">
                  <div className="cal-detail-icon" style={{ background: row.bg, color: row.color }}>
                    <i className={`bi bi-${row.icon}`}></i>
                  </div>
                  <div className="cal-detail-content">
                    <div className="cal-detail-label">{row.label}</div>
                    <div className="cal-detail-value">{row.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {derivedStatus === 'completed' && (
              <button className="cal-action-btn cal-action-btn--analytics" onClick={() => viewAnalytics(selectedEvent.id)}>
                <i className="bi bi-bar-chart-line-fill"></i> View Analytics
              </button>
            )}
            {derivedStatus === 'completed' && !derivedFilename && (
              <p className="cal-no-file"><i className="bi bi-info-circle"></i> No log file available for this experiment</p>
            )}
            {derivedStatus === 'missed' && (
              <button className="cal-action-btn cal-action-btn--reschedule" onClick={() => openRescheduleModal(selectedEvent.id, derivedDateRaw)}>
                <i className="bi bi-arrow-clockwise"></i> Reschedule Experiment
              </button>
            )}
          </div>
        </ModalOverlay>
      )}

      {/* ════════ RESCHEDULE MODAL ════════ */}
      {showRescheduleModal && (
        <ModalOverlay onClose={() => { setShowRescheduleModal(false); setRescheduleScheduleId(null); setNewScheduleDate(''); }}>
          <div className="cal-modal-header" style={{ background:'linear-gradient(135deg, #FF5722, #FF7043)' }}>
            <div>
              <h3 className="cal-modal-title"><i className="bi bi-arrow-clockwise"></i> Reschedule Experiment</h3>
              <p className="cal-modal-sub">Choose a new date for this experiment</p>
            </div>
            <button className="cal-modal-close" style={{ color:'white', background:'rgba(255,255,255,0.2)' }} onClick={() => { setShowRescheduleModal(false); setRescheduleScheduleId(null); setNewScheduleDate(''); }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div className="cal-modal-body">
            <div className="cal-reschedule-current">
              <span className="cal-reschedule-label"><i className="bi bi-calendar-x-fill"></i> Current (Missed)</span>
              <span className="cal-reschedule-date">{formatDate(currentScheduleDate)}</span>
            </div>
            <div className="cal-form-group">
              <label className="cal-form-label"><i className="bi bi-calendar-check-fill" style={{ color:'#27ae60' }}></i> New Schedule Date *</label>
              <input type="date" className="cal-form-control" value={newScheduleDate} onChange={e => setNewScheduleDate(e.target.value)} min={new Date().toISOString().split('T')[0]} required />
            </div>
            <div className="cal-note-box">
              <i className="bi bi-lightbulb-fill"></i>
              <span>Rescheduling will update the experiment date and notify the assigned user.</span>
            </div>
          </div>
          <div className="cal-modal-footer">
            <button className="cal-btn-cancel" onClick={() => { setShowRescheduleModal(false); setRescheduleScheduleId(null); setNewScheduleDate(''); }}>
              <i className="bi bi-x-circle"></i> Cancel
            </button>
            <button className="cal-btn-submit" style={{ flex:'none', padding:'0.6rem 1.5rem' }} onClick={handleReschedule}>
              <i className="bi bi-check-circle-fill"></i> Confirm Reschedule
            </button>
          </div>
        </ModalOverlay>
      )}

      {/* Tooltip portal target */}
    </QuratorLayout>
  );
};

export default Calendar;
